import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import BrandingModal from "../components/BrandingModal";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState([]);
  const [orgs, setOrgs] = useState([]);  // full org docs incl. logo_url
  const [trendsByOrg, setTrendsByOrg] = useState({});  // {org_id: usable_wave_count}
  const [error, setError] = useState("");

  async function refreshOrgs() {
    const { data } = await api.get("/api/organizations");
    setOrgs(data.organizations || []);
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/deployments");
        setDeployments(data.deployments || []);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
    (async () => {
      try {
        await refreshOrgs();
      } catch (_) {
        // non-fatal — branding section just hides if orgs fail
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/organizations/trends-summary");
        const map = {};
        (data.summaries || []).forEach((s) => { map[s.org_id] = s.usable_wave_count; });
        setTrendsByOrg(map);
      } catch (_) {
        // non-fatal — dashboard still renders without the trends links
      }
    })();
  }, []);

  const openCount = deployments.filter((d) => d.status === "open").length;

  // Build a unique org list from the deployments + filter to orgs with >=2
  // usable waves (>=5 completed respondents each). This is the trigger for
  // surfacing the "View trends" link.
  const orgsWithTrends = (() => {
    const seen = new Map();
    for (const d of deployments) {
      if (!d.org_id) continue;
      if (!seen.has(d.org_id)) seen.set(d.org_id, { id: d.org_id, name: d.org_name });
    }
    return [...seen.values()].filter((o) => (trendsByOrg[o.id] || 0) >= 2);
  })();

  // Sprint 8 — every unique org in the deployment list, with its primary color.
  // Surfaced as a small "Branding" section so each org can carry its own
  // accent color on the PDF cover.
  // Sprint 9 — merge logo_url from /api/organizations so the branding row
  // can show a thumbnail and the modal can pre-fill the current logo state.
  const allOrgs = (() => {
    const orgsById = new Map();
    for (const o of orgs) orgsById.set(o.id, o);
    const seen = new Map();
    for (const d of deployments) {
      if (!d.org_id) continue;
      if (!seen.has(d.org_id)) {
        const fromList = orgsById.get(d.org_id) || {};
        seen.set(d.org_id, {
          id: d.org_id,
          name: d.org_name,
          primary_color: d.org_primary_color || fromList.primary_color || "#4592A8",
          logo_url: fromList.logo_url || null,
        });
      }
    }
    return [...seen.values()];
  })();

  const [brandingOrg, setBrandingOrg] = useState(null); // {id, name, primary_color, logo_url}

  async function saveBrandingColor(orgId, color) {
    await api.patch(`/api/organizations/${orgId}`, { primary_color: color });
    // Refresh deployments + orgs so the dashboard reflects the new color.
    const { data } = await api.get("/api/deployments");
    setDeployments(data.deployments || []);
    await refreshOrgs();
  }

  async function saveBrandingLogo(orgId, file) {
    const form = new FormData();
    form.append("file", file);
    await api.post(`/api/organizations/${orgId}/logo`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    await refreshOrgs();
  }

  async function clearBrandingLogo(orgId) {
    await api.delete(`/api/organizations/${orgId}/logo`);
    await refreshOrgs();
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="dashboard-page">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-sm text-gray-700 hidden md:block">
              {user?.name}{" "}
              <span className="text-gray-500">·</span>{" "}
              <span className="uppercase tracking-widest text-xs" style={{ fontFamily: "Oswald" }}>
                {user?.role?.replace("_", " ")}
              </span>
            </div>
            {(user?.role === "super_admin" || user?.role === "consultant") && (
              <Link
                to="/dashboard/admin/coaching-reports"
                data-testid="admin-coaching-reports-link"
                className="btn-quiet"
                style={{ textDecoration: "none" }}
              >
                Coaching reports
              </Link>
            )}
            {user?.role === "super_admin" && (
              <Link
                to="/dashboard/admin/cleanup"
                data-testid="admin-cleanup-link"
                className="btn-quiet"
                style={{ textDecoration: "none" }}
              >
                Admin · Cleanup
              </Link>
            )}
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-8 py-12">
        <div className="mb-12 fade-up flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="eyebrow mb-3">Consultant console</div>
            <h1 style={{ fontSize: "clamp(2.5rem, 5vw, 3.75rem)" }} className="mb-3">
              Deployments.
            </h1>
            <p className="lead text-lg md:text-xl max-w-prose">
              Open a deployment to view its analytics, or create a new deployment to start a wave.
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/deployments/new")}
            data-testid="new-deployment-button"
            style={{
              fontFamily: "Oswald",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontSize: "11px",
              color: "#FBFAF7",
              background: "#AD393E",
              border: 0,
              padding: "12px 22px",
              cursor: "pointer",
            }}
          >
            + New deployment
          </button>
        </div>

        {error && (
          <div className="panel mb-8" style={{ borderColor: "#AD393E" }} data-testid="error-banner">
            <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Couldn&rsquo;t load deployments</div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        {orgsWithTrends.length > 0 && (
          <section className="mb-12" data-testid="orgs-trends-section">
            <div className="eyebrow mb-3">Multi-wave trends</div>
            <div className="space-y-3">
              {orgsWithTrends.map((o) => (
                <Link
                  key={o.id}
                  to={`/dashboard/orgs/${o.id}/trends`}
                  className="flex items-center justify-between border border-gray-100 px-5 py-4 hover:border-teal transition-colors duration-100"
                  data-testid={`org-trends-link-${o.id}`}
                >
                  <div>
                    <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>{o.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                      {trendsByOrg[o.id]} usable wave{trendsByOrg[o.id] === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span
                    className="text-xs px-3 py-1"
                    style={{
                      fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                      color: "#2F6B7C", border: "1px solid #4592A8",
                    }}
                  >
                    View trends →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {allOrgs.length > 0 && (
          <section className="mb-12" data-testid="org-branding-section">
            <div className="eyebrow mb-3">Client branding</div>
            <div className="space-y-3">
              {allOrgs.map((o) => (
                <div
                  key={o.id}
                  className="flex items-center justify-between border border-gray-100 px-5 py-4"
                  data-testid={`org-branding-row-${o.id}`}
                >
                  <div className="flex items-center gap-4">
                    {o.logo_url ? (
                      <img
                        src={o.logo_url}
                        alt={`${o.name} logo`}
                        style={{
                          width: 48, height: 24, objectFit: "contain",
                          background: "white", border: "1px solid rgba(0,0,0,0.08)",
                          padding: 2,
                        }}
                        data-testid={`org-branding-logo-${o.id}`}
                      />
                    ) : (
                      <span
                        className="inline-block"
                        style={{
                          width: 24, height: 24,
                          background: o.primary_color,
                          border: "1px solid rgba(0,0,0,0.08)",
                        }}
                        role="img"
                        aria-label={`Current primary color ${o.primary_color}`}
                        data-testid={`org-branding-swatch-${o.id}`}
                      />
                    )}
                    <div>
                      <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>{o.name}</div>
                      <div className="text-xs text-gray-600 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                        {o.logo_url ? "Logo set" : "Logo not set"} &middot; Cover accent {o.primary_color}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setBrandingOrg(o)}
                    data-testid={`org-branding-edit-${o.id}`}
                    style={{
                      fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                      fontSize: "11px", color: "#2F6B7C", border: "1px solid #2F6B7C",
                      background: "transparent", padding: "8px 14px", cursor: "pointer",
                    }}
                  >
                    Edit branding
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mb-16" data-testid="deployments-section">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="eyebrow mb-2">All deployments</div>
              <h2 style={{ fontSize: "1.75rem" }}>
                {deployments.length} total{openCount > 0 ? ` · ${openCount} open` : ""}
              </h2>
            </div>
          </div>

          {deployments.length === 0 ? (
            <div className="panel" data-testid="no-deployments">
              <div className="eyebrow mb-2">No deployments yet</div>
              <p className="text-sm text-gray-700 max-w-prose">
                Create a deployment to begin. Each deployment configures the demographic segmentation
                for one wave of the 56-item assessment, generates per-respondent survey URLs, and
                produces the heatmap, rubric, and PDF report when responses come in.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {deployments.map((dep) => (
                <Link
                  key={dep.id}
                  to={`/dashboard/deployments/${dep.id}`}
                  className="block panel hover:border-teal transition-colors duration-100"
                  data-testid={`deployment-${dep.id}`}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <div>
                      <div className="eyebrow mb-1">{dep.org_name} · Wave {dep.wave_number}</div>
                      <h3 style={{ fontSize: "1.5rem" }}>{dep.name}</h3>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-gray-700" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
                        {dep.completed}/{dep.invited} COMPLETE
                      </div>
                      {dep.deployment_mode === "individual" && (
                        <span
                          className="text-xs uppercase tracking-widest px-3 py-1"
                          data-testid={`individual-badge-${dep.id}`}
                          title="Personal Resilience Index — 1:1 coaching engagement"
                          style={{
                            fontFamily: "Oswald",
                            background: "#E3ECFE",
                            color: "#2563EB",
                          }}
                        >
                          PRI
                        </span>
                      )}
                      <span
                        className="text-xs uppercase tracking-widest px-3 py-1"
                        style={{
                          fontFamily: "Oswald",
                          background:
                            dep.status === "open" ? "#D8E9EF" :
                            dep.status === "draft" ? "#FAF7F1" :
                            "#EEEEEE",
                          color:
                            dep.status === "open" ? "#2F6B7C" :
                            dep.status === "draft" ? "#AD393E" :
                            "#555555",
                        }}
                      >
                        {dep.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      {brandingOrg && (
        <BrandingModal
          org={brandingOrg}
          onClose={() => setBrandingOrg(null)}
          onSaveColor={async (color) => {
            await saveBrandingColor(brandingOrg.id, color);
          }}
          onUploadLogo={async (file) => {
            await saveBrandingLogo(brandingOrg.id, file);
            // Patch the modal's local org snapshot so the preview updates
            // without forcing the user to reopen the modal.
            const { data } = await api.get("/api/organizations");
            const fresh = (data.organizations || []).find((o) => o.id === brandingOrg.id);
            if (fresh) setBrandingOrg({ ...brandingOrg, logo_url: fresh.logo_url });
          }}
          onClearLogo={async () => {
            await clearBrandingLogo(brandingOrg.id);
            setBrandingOrg({ ...brandingOrg, logo_url: null });
          }}
          onDone={() => setBrandingOrg(null)}
        />
      )}
    </div>
  );
}
